import { CartItemProps, IGlobalData, UserAuthProps } from "./IGlobalData";
import { createContext, useState, useContext, useEffect } from "react";
import { getValidToken, clearAuthData } from "../utils/auth";

const GlobalContext = createContext<IGlobalData>({} as IGlobalData);

export default GlobalContext;

export function GlobalProvider({ children }: React.PropsWithChildren) {
  const [number, setNumber] = useState(1);
  const [accountNumber, setAccountNumber] = useState("");
  const [user, setUser] = useState<UserAuthProps | null>(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);

  useEffect(() => {
    const validToken = getValidToken();
    if (validToken) {
      const savedUserData = localStorage.getItem("userData");
      if (savedUserData) {
        try {
          const userData = JSON.parse(savedUserData);
          setUser({ ...userData, access_token: validToken });
        } catch (error) {
          clearAuthData();
        }
      }
    }

    setIsSessionLoading(false);
  }, []);

  const increaseValue = () => setNumber((prev) => prev + 1);
  const decreaseValue = () => setNumber((prev) => (prev > 0 ? prev - 1 : 0));

  const [cartList, setCartList] = useState<CartItemProps[]>([]);

  const login = (userData: UserAuthProps) => {
    setUser(userData);
    const { ...userDataToSave } = userData;
    localStorage.setItem("userData", JSON.stringify(userDataToSave));
  };

  const logout = () => {
    setUser(null);
    clearAuthData();
    localStorage.removeItem("userData");
  };

  const addCartItem = (item: CartItemProps) => {
    setCartList((prevCart) => [...prevCart, item]);
  };

  const removeCartItem = (id: number) => {
    setCartList((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  const updateQuantity = (id: number, amount: number, type: string) => {
    setCartList((prevCart) =>
      prevCart
        .map((item) =>
          item.id === id
            ? {
                ...item,
                quantity: Math.max(
                  0,
                  item.quantity + amount * (type === "+" ? 1 : -1)
                ),
              }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const totalQuantity = () => {
    let total = 0;
    cartList.forEach((item) => (total += item.quantity));
    return total;
  };

  const data = {
    user,
    setUser,
    cartList,
    setCartList,
    addCartItem,
    removeCartItem,
    updateQuantity,
    totalQuantity,
    accountNumber,
    setAccountNumber,
    login,
    logout,
    number,
    increaseValue,
    decreaseValue,
    isSessionLoading,
  };

  return (
    <GlobalContext.Provider value={data}>{children}</GlobalContext.Provider>
  );
}

export function useGlobalData() {
  return useContext(GlobalContext);
}
